import { FieldErrors, Path, PathValue, UseFormSetValue } from 'react-hook-form'
import { Autocomplete, TextField } from '@mui/material'
import TextFieldWrapper, { TextFieldWrapperProps } from '../TextFieldWrapper/TextFieldWrapper'
import { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { CaretDown } from 'ComponentLibrary/src/atoms/Select/Select'

type AutoCompleteFieldValues = {
    [x: string]: string | string[]
}

type FormTextWithPreSelectsProps<TFieldValues extends AutoCompleteFieldValues> = {
    label?: string
    className?: string
    disabled?: boolean
    required?: boolean
    inputFieldStyles?: CSSProperties
    placeholder?: string
    freeSolo?: boolean
    multiline?: boolean
    options: string[]
    setValue: UseFormSetValue<TFieldValues>
    errors?: FieldErrors<TFieldValues>
    defaultValue: PathValue<TFieldValues, Path<TFieldValues>>
    fieldName: Path<TFieldValues>
    multiple?: boolean
    emptyValueLabel?: string
    endAdornment?: ReactNode
    disableClearable?: boolean
    renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: string) => ReactNode
} & TextFieldWrapperProps

export default function FormTextWithPreSelects<TFieldValues extends AutoCompleteFieldValues>({
    xs = 12,
    options,
    fullWidth,
    label,
    required,
    hideLabel,
    fieldName,
    freeSolo,
    multiline,
    setValue,
    defaultValue,
    multiple,
    placeholder,
    className,
    inputFieldStyles,
    renderOption,
    endAdornment,
    emptyValueLabel = 'Keine Auswahl',
    disableClearable,
}: FormTextWithPreSelectsProps<TFieldValues>) {
    const defaultRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: string) => (
        <li {...props}>{option !== '' ? option : emptyValueLabel}</li>
    )

    return (
        <TextFieldWrapper fullWidth={fullWidth} xs={xs} label={label} required={required} hideLabel={hideLabel}>
            <Autocomplete
                className={className}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            sx={inputFieldStyles}
                            placeholder={placeholder}
                            label={label}
                            multiline={multiline}
                            fullWidth={fullWidth}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment,
                            }}
                        />
                    )
                }}
                options={!required && !multiple ? ['', ...options] : options}
                onChange={(_, value) => {
                    setValue(fieldName, value as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true })
                }}
                renderOption={renderOption || defaultRenderOption}
                freeSolo={freeSolo}
                multiple={multiple}
                ChipProps={{
                    sx: {
                        height: 'auto',
                        '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                        },
                    },
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && event.shiftKey) {
                        event.defaultMuiPrevented = true
                    }
                }}
                defaultValue={defaultValue}
                clearOnBlur
                disableClearable={disableClearable}
                includeInputInList
                autoSelect
                popupIcon={<CaretDown />}
            />
        </TextFieldWrapper>
    )
}
